import Notifications from 'react-notification-system-redux';
import { api } from '../../../general/apiScenarios';
import { pagePathChanged } from '../../../general/modules/router/actions';
import { CHNode } from '../../../general/node';
import { nonNull } from '../../../general/utils/nonNull';
import { ListenerApiLight, _select, lm } from '../../listenerMiddleware';
import { logger } from '../../logger';
import { Page } from '../../models/page';
import { getNodeId } from '../app/selectors';
import { getCurrentNode } from '../breadcrumbs/selectors';
import { deleteSuccess } from '../remover/actions';
import { nodeChanged } from '../renaming/actions';
import * as favoriteActions from './actions';
import { Favorite, Messages } from './model';
import {
  getBaseIdsInProgress,
  getFavoriteById,
  getFavoriteByNodeId,
  getIsToggleOn,
  getRenamingId,
  getRenamingText,
} from './selectors';

export function favoriteRootListener() {
  watchAppPagePathChanged();
  watchDeleteClicked();
  watchRenameCanceled();
  watchRenameConfirmed();
  watchToggleClicked();
}

export function watchAppPagePathChanged() {
  lm.takeLatest([pagePathChanged, nodeChanged, deleteSuccess], async (action, lapi) => {
    if (action.type === pagePathChanged.type) {
      const { page } = action.payload;
      if (
        ([Page.FAVORITES, Page.FOLDER, Page.SEARCH, Page.SHARED_LINKS] as string[]).includes(page)
      ) {
        // eslint-disable-next-line @myua/rtk-helper/check-signal-aborted
        await loadFavorites(lapi);
      }
    } else {
      // eslint-disable-next-line @myua/rtk-helper/check-signal-aborted
      await loadFavorites(lapi);
    }
  });
}

async function loadFavorites(lapi: ListenerApiLight) {
  lapi.dispatch(favoriteActions.loadFavoritesStarted());
  const result = await api.fav.load();
  if (lapi.signal.aborted) return;
  if (result.success) {
    lapi.dispatch(favoriteActions.loadFavoritesSuccess({ list: result.data.response }));
  } else {
    lapi.dispatch(favoriteActions.loadFavoritesError({ error: result.reason.message }));
    lapi.dispatch(Notifications.error(Messages.loadFavoritesError()));
  }
}

function watchToggleClicked() {
  lm.takeEvery(favoriteActions.toggleClicked, async (_, lapi) => {
    const nodeID = _select(lapi, getNodeId);
    const baseIdsInProgress: ReturnType<typeof getBaseIdsInProgress> = _select(
      lapi,
      getBaseIdsInProgress,
    );
    if (nodeID === null) return;
    if (baseIdsInProgress.filter(nonNull).includes(nodeID)) {
      return;
    }

    const isToggleOn: ReturnType<typeof getIsToggleOn> = _select(lapi, getIsToggleOn);
    if (isToggleOn) {
      const favorites: ReturnType<typeof getFavoriteByNodeId> = _select(lapi, getFavoriteByNodeId);
      const favorite = favorites[nodeID];
      await deleteFavorite(lapi, favorite);
    } else {
      const node: ReturnType<typeof getCurrentNode> = _select(lapi, getCurrentNode);
      if (node) {
        await addFavorite(lapi, node);
      }
    }
  });
}

function watchDeleteClicked() {
  lm.takeEvery(favoriteActions.deleteClicked, async (action, lapi) => {
    const { favoriteId } = action.payload;
    const favorites = _select(lapi, getFavoriteById);
    const favorite = favorites[favoriteId];
    const baseIdsInProgress: ReturnType<typeof getBaseIdsInProgress> = _select(
      lapi,
      getBaseIdsInProgress,
    );
    if (baseIdsInProgress.includes(favorite.node_id)) {
      return;
    }

    await deleteFavorite(lapi, favorite);
  });
}

async function addFavorite(lapi: ListenerApiLight, node: CHNode) {
  lapi.dispatch(favoriteActions.addFavoriteStarted({ nodeId: node.id }));

  const result = await api.fav.add(node.id, node.name);
  if (lapi.signal.aborted) return;
  if (result.success) {
    logger.lapi(lapi, {
      action: 'Favorites',
      sub_action: 'Add',
      extra: { int_1: node.id, int_2: result.data.response.id },
    });
    lapi.dispatch(
      favoriteActions.addFavoriteSuccess({ fav: result.data.response, nodeId: node.id }),
    );
  } else {
    lapi.dispatch(
      favoriteActions.addFavoriteError({ error: result.reason.message, nodeId: node.id }),
    );
    lapi.dispatch(Notifications.error(Messages.addFavoriteError()));
  }
}

async function deleteFavorite(lapi: ListenerApiLight, favorite: Favorite) {
  lapi.dispatch(favoriteActions.deleteFavoriteStarted({ favorite }));
  const result = await api.fav.delete(favorite.id);
  if (lapi.signal.aborted) return;
  if (result.success) {
    lapi.dispatch(favoriteActions.deleteFavoriteSuccess({ result, favorite }));
    logger.lapi(lapi, {
      action: 'Favorites',
      sub_action: 'Delete',
      extra: {
        int_1: favorite.node_id,
        int_2: favorite.id,
        int_3: favorite.id,
        text_1: favorite.name,
      },
    });
  } else {
    lapi.dispatch(favoriteActions.deleteFavoriteError({ favorite }));
    lapi.dispatch(Notifications.error(Messages.deleteFavoriteError()));
  }
}

function watchRenameCanceled() {
  lm.takeEvery(favoriteActions.renameCanceled, (_, lapi) => {
    lapi.dispatch(favoriteActions.renameInputHidden());
  });
}

function watchRenameConfirmed() {
  lm.takeEvery(favoriteActions.renameConfirmed, async (_, lapi) => {
    const favoriteId = _select(lapi, getRenamingId);
    if (favoriteId === null) return;
    const name = _select(lapi, getRenamingText);

    const favorites = _select(lapi, getFavoriteById);
    const oldName = favorites[favoriteId].name;

    lapi.dispatch(favoriteActions.renamed({ favoriteId, name }));
    lapi.dispatch(favoriteActions.renameInputHidden());
    lapi.dispatch(favoriteActions.renameStarted({ favoriteId }));

    const { success } = await api.fav.rename(favoriteId, name);
    if (lapi.signal.aborted) return;
    if (!success) {
      lapi.dispatch(favoriteActions.renamed({ favoriteId, name: oldName }));
      lapi.dispatch(Notifications.error(Messages.renameFavoriteError()));
    }

    lapi.dispatch(favoriteActions.renameFinished({ favoriteId }));
  });
}
